'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { BookOpen, Loader2 } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { Button } from '@/shared/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet';
import { getReusableBasis, getBasisPreview, type BasisPreview } from '@/contexts/publishing/actions/publicGallery';
import type { BasisItem } from '@/contexts/publishing';

type Source = BasisItem['source'];

/** Cards shown per shelf before "show more" — 44 classics at once read as a wall. */
const PAGE = 8;
const AGE_ORDER = ['1-3', '3-5', '5-7', '7-10', '10+'];

/**
 * Basis picker for from-text `based_on` — seed a new story from a ready text
 * ("the same story, but with my heroes"). Two shelves: the curated public-domain
 * classics (admin `basis_texts`) and stories other parents published to the
 * gallery and let others reuse. Any basis can be read in full in a side sheet
 * before picking. Selecting sets `basisStoryId`; the engine loads the text
 * server-side.
 */
export function BasisPicker({
  value,
  language,
  ageGroup,
  onChange,
  onPick,
}: {
  value?: string;
  /** Story language — the list is narrowed to bases in this language. */
  language?: string;
  /** Wizard's age group — preselects the age filter (user can widen it). */
  ageGroup?: string;
  /** Sets the basisStoryId AND (on select) the picked title — in ONE call, so the
   *  parent applies a single setParameters. Two separate parent writes from the same
   *  render's stale snapshot clobbered basisStoryId (first click never "stuck"). */
  onChange: (basisStoryId: string | undefined, title?: string) => void;
  /** Full selected item (or undefined on deselect) — carries the basis cast for hero-swap. */
  onPick?: (item: BasisItem | undefined) => void;
}) {
  const t = useTranslations('content.wizard.storyInput.basis');
  const tAge = useTranslations('content.wizard.ageGroups');
  const [items, setItems] = useState<BasisItem[] | null>(null);
  // Age filter: starts on the wizard's age group (if any tale has it), 'all' otherwise.
  const [age, setAge] = useState<string>('all');
  const [ageTouched, setAgeTouched] = useState(false);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<Source>('admin');
  const [reading, setReading] = useState<BasisItem | null>(null);
  const [limit, setLimit] = useState<Record<Source, number>>({ admin: PAGE, story: PAGE });

  useEffect(() => {
    let alive = true;
    setItems(null);
    getReusableBasis(language)
      .then((r) => alive && setItems(r))
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, [language]);

  // Rehydrate the parent's cast state for the ALREADY-selected basis once items
  // load. StoryInputStep is unmounted when the wizard leaves the step, so its
  // basisCast local state is lost; without this, coming back (basisStoryId still
  // set in the parent params) showed no cast until the user re-clicked the basis.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  useEffect(() => {
    if (!items || !value) return;
    const match = items.find((b) => b.id === value);
    if (match) {
      onPickRef.current?.(match);
      setTab(match.source);
    }
  }, [items, value]);

  // Preselect the wizard's age group once, if that shelf isn't empty for it.
  useEffect(() => {
    if (!items || ageTouched) return;
    setAge(ageGroup && items.some((b) => b.ageGroup === ageGroup) ? ageGroup : 'all');
  }, [items, ageGroup, ageTouched]);

  // Land on the shelf that has something (classics first).
  useEffect(() => {
    if (!items || value) return;
    if (!items.some((b) => b.source === 'admin') && items.some((b) => b.source === 'story')) setTab('story');
  }, [items, value]);

  if (items === null) {
    return (
      <div className="space-y-2" data-testid="basis-picker-loading">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          <span>{t('loading')}</span>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="h-16 animate-pulse rounded-lg border bg-muted/40" />
          <div className="h-16 animate-pulse rounded-lg border bg-muted/40" />
        </div>
      </div>
    );
  }
  if (items.length === 0) return null; // nothing curated yet → hide the picker entirely

  const select = (b: BasisItem) => {
    const selected = value === b.id;
    const next = selected ? undefined : b.id;
    onChange(next, next ? b.title : undefined);
    onPick?.(next ? b : undefined);
  };

  const q = query.trim().toLowerCase();
  const matches = (b: BasisItem) =>
    (age === 'all' || b.ageGroup === age) &&
    (!q || b.title.toLowerCase().includes(q) || b.excerpt.toLowerCase().includes(q));
  const bySource = (src: Source) => items.filter((b) => b.source === src && matches(b));
  const counts = { admin: bySource('admin').length, story: bySource('story').length };
  const ages = AGE_ORDER.filter((a) => items.some((b) => b.ageGroup === a));

  const renderList = (src: Source) => {
    const all = bySource(src);
    const selIdx = value ? all.findIndex((b) => b.id === value) : -1;
    const shown = all.slice(0, Math.max(limit[src], selIdx + 1));
    const rest = all.length - shown.length;
    if (all.length === 0) {
      return (
        <p className="text-[11px] text-muted-foreground py-2" data-testid={`basis-empty-${src}`}>
          {q || age !== 'all' ? t('nothingFound') : src === 'admin' ? t('emptyAdmin') : t('emptyStory')}
        </p>
      );
    }
    return (
      <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {shown.map((b) => {
          const selected = value === b.id;
          return (
            <div
              key={b.id}
              className={cn(
                'flex flex-col rounded-lg border-2 p-2 text-left transition-colors',
                selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-primary/40',
              )}
              data-testid={`basis-option-${b.id}`}
              data-selected={selected || undefined}
            >
              <button type="button" onClick={() => select(b)} className="text-left" data-testid={`basis-select-${b.id}`}>
                <p className="text-sm font-medium line-clamp-1">{b.title}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground line-clamp-2">{b.excerpt}</p>
              </button>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="text-[10px] text-muted-foreground">
                  {b.language}
                  {b.ageGroup ? ` · ${b.ageGroup}` : ''}
                  {b.characters.length > 0 ? ` · ${t('castCount', { count: b.characters.length })}` : ''}
                </p>
                <button
                  type="button"
                  onClick={() => setReading(b)}
                  className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                  data-testid={`basis-read-${b.id}`}
                >
                  <BookOpen className="size-3" />
                  {t('read')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {rest > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setLimit((l) => ({ ...l, [src]: l[src] + PAGE }))}
          data-testid={`basis-more-${src}`}
        >
          {t('showMore', { count: Math.min(rest, PAGE), rest })}
        </Button>
      )}
      </div>
    );
  };

  return (
    <div className="space-y-2" data-testid="basis-picker">
      <p className="text-xs text-muted-foreground">{t('intro')}</p>
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setLimit({ admin: PAGE, story: PAGE });
        }}
        placeholder={t('searchPlaceholder')}
        className="w-full rounded border bg-background px-2 py-1 text-xs"
        data-testid="basis-search"
      />
      {ages.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="basis-age-filter">
          <span className="text-[11px] text-muted-foreground">{t('ageFilter')}</span>
          {['all', ...ages].map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => {
                setAge(a);
                setAgeTouched(true);
                setLimit({ admin: PAGE, story: PAGE });
              }}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-[11px] transition-colors',
                age === a ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/40',
              )}
              data-testid={`basis-age-${a}`}
            >
              {a === 'all' ? t('ageAll') : tAge(a)}
            </button>
          ))}
        </div>
      )}
      <Tabs value={tab} onValueChange={(v) => setTab(v as Source)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="admin" data-testid="basis-tab-admin">
            {t('tabAdmin')} ({counts.admin})
          </TabsTrigger>
          <TabsTrigger value="story" data-testid="basis-tab-story">
            {t('tabStory')} ({counts.story})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="admin" className="mt-2">
          {renderList('admin')}
        </TabsContent>
        <TabsContent value="story" className="mt-2">
          <p className="mb-2 text-[11px] text-muted-foreground">{t('storyHint')}</p>
          {renderList('story')}
        </TabsContent>
      </Tabs>

      <BasisReaderSheet
        item={reading}
        selected={!!reading && value === reading.id}
        onClose={() => setReading(null)}
        onUse={(b) => {
          if (value !== b.id) select(b);
          setReading(null);
        }}
      />
    </div>
  );
}

/** Side sheet with the full text + cast of one basis; "use as basis" from here. */
function BasisReaderSheet({
  item,
  selected,
  onClose,
  onUse,
}: {
  item: BasisItem | null;
  selected: boolean;
  onClose: () => void;
  onUse: (b: BasisItem) => void;
}) {
  const t = useTranslations('content.wizard.storyInput.basis');
  const [preview, setPreview] = useState<BasisPreview | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!item) return;
    let alive = true;
    setPreview(null);
    setFailed(false);
    getBasisPreview(item.id)
      .then((p) => {
        if (!alive) return;
        if (p) setPreview(p);
        else setFailed(true);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [item]);

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl" data-testid="basis-reader">
        <SheetHeader className="border-b px-5 py-4 text-left sm:px-8">
          <SheetTitle className="pr-8 text-lg">{item?.title}</SheetTitle>
          {item && (
            <p className="text-xs text-muted-foreground">
              {item.source === 'admin' ? t('sourceAdmin') : t('sourceStory')} · {item.language}
              {item.ageGroup ? ` · ${item.ageGroup}` : ''}
            </p>
          )}
        </SheetHeader>
        {/* Book-like column: capped measure, generous side padding + line height. */}
        <div
          className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6 text-[15px] leading-7 sm:text-base sm:leading-8"
          data-testid="basis-reader-text"
        >
          <div className="mx-auto max-w-[60ch]">
          {preview?.characters.length ? (
            <div className="mb-5 rounded-md border bg-muted/30 px-3 py-2 text-xs leading-5" data-testid="basis-reader-cast">
              <p className="mb-1 font-medium">{t('castLabel')}</p>
              <ul className="space-y-0.5">
                {preview.characters.map((c) => (
                  <li key={c.name}>
                    <span className="font-medium">{c.name}</span>
                    {c.description ? <span className="text-muted-foreground"> — {c.description}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {!preview && !failed && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              <span>{t('loadingText')}</span>
            </div>
          )}
          {failed && <p className="text-xs text-destructive">{t('loadFailed')}</p>}
          {preview &&
            preview.text.split(/\n\s*\n/).map((para, i) => (
              <p key={i} className={cn('mb-4 whitespace-pre-line', para.includes('\n') && 'pl-4 italic text-muted-foreground')}>
                {para}
              </p>
            ))}
          </div>
        </div>
        <div className="flex gap-2 border-t px-5 py-3 sm:px-8">
          <Button size="sm" className="flex-1" disabled={!item} onClick={() => item && onUse(item)} data-testid="basis-reader-use">
            {selected ? t('useSelected') : t('use')}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            {t('close')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
